import { OverflowGrid } from "../../v2/components/ListOverflow.jsx";
// src/features/imports/PendingImportsQueue.jsx
//
// LSE-ADD-DATA-MODAL-S2 — Queue of staged import sessions waiting for review.
// INGEST-PENDING-DISCARD — Per-row Discard button + bulk Discard all action.
//
// Reachable from:
//   • Leases landing "Pending imports (N)" button
//
// Props:
//   onOpenSession  (sessionId) => void — navigate to ImportSessionReview
//   onBack         () => void          — return to Leases landing

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { T } from "../../app/tokens.js";
import { Card } from "../../components/Card.jsx";
import { SearchInput } from "../../v2/components/SearchInput.jsx";
import { matchesFoldedQuery } from "../../utils/textSearch.js";
import {
  listImportSessions,
  discardImportSession,
  discardAllPendingSessions,
} from "../../data/importSessionsClient.js";

// ─── Status pill ──────────────────────────────────────────────────────────────

// Reuses the app's standard RAG (red/amber/green) pill palette — the same
// tokens used by IngestionHistoryPanel's StatusPill — instead of a bespoke
// blue/amber scheme, so "Applied"/"Rejected"/etc. read consistently across
// admin surfaces.
const STATUS_PILL = {
  staged:            { bg: T.ragAmberBg, color: T.ragAmber, label: "Staged"     },
  in_review:         { bg: T.ragAmberBg, color: T.ragAmber, label: "In review"  },
  applied:           { bg: T.ragGreenBg, color: T.green,    label: "Applied"    },
  partially_applied: { bg: T.ragAmberBg, color: T.ragAmber, label: "Partial"    },
  rejected:          { bg: T.ragRedBg,   color: T.ragRed,   label: "Rejected"   },
  uploading:         { bg: T.lightGray,  color: T.mid,      label: "Uploading…" },
  expired:           { bg: T.lightGray,  color: T.light,    label: "Expired"    },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || { bg: T.lightGray, color: T.mid, label: status };
  return (
    <span
      data-testid={`status-pill-${status}`}
      style={{
        display: "inline-flex", alignItems: "center",
        fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6,
        background: s.bg, color: s.color, whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Source type labels ───────────────────────────────────────────────────────

const SOURCE_LABELS = {
  spacebase_main_leases:      "Spacebase - Main Leases",
  spacebase_critical_dates:   "Spacebase - Critical Dates",
  spacebase_recurring_leases: "Spacebase - Recurring Leases",
  spacebase_contacts:         "Spacebase - Contacts",
  spacebase_documents:        "Spacebase - Documents",
  pdf_abstraction:            "PDF Abstraction",
  manual_csv:                 "Manual CSV",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return String(iso); }
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel, busy }) {
  return (
    <div
      data-testid="discard-confirm-dialog"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <div
        style={{
          background: T.white, borderRadius: 10, padding: "28px 32px",
          maxWidth: 420, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        }}
      >
        <p
          style={{ margin: "0 0 20px", fontSize: T.fsBase, color: T.dark, lineHeight: 1.5 }}
          data-testid="discard-confirm-message"
        >
          {message}
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid="discard-cancel-btn"
            disabled={busy}
            onClick={onCancel}
            style={{
              padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm,
              fontWeight: T.fwSemibold, border: `1.5px solid ${T.border}`,
              background: T.bg, color: T.dark, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="discard-confirm-btn"
            disabled={busy}
            onClick={onConfirm}
            style={{
              padding: "7px 16px", borderRadius: 6, fontSize: T.fsSm,
              fontWeight: T.fwSemibold, border: "none",
              background: T.red, color: T.white,
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Discarding…" : "Discard"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Session row ──────────────────────────────────────────────────────────────

const DISCARDABLE_STATUSES = new Set(["staged", "in_review", "expired"]);

// Standard row height/padding + uppercase-header card-table language shared
// with Ingestion history / Offices (Card wrapper + 1px row dividers).
function SessionRow({ session, onOpenSession, onDiscard }) {
  const isExpired   = session.status === "expired";
  const canReview   = ["staged", "in_review", "partially_applied"].includes(session.status);
  const canDiscard  = DISCARDABLE_STATUSES.has(session.status);
  return (
    <div
      data-testid={`session-row-${session.id}`}
      data-list-row=""
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 16px",
        borderBottom: `1px solid ${T.border}`,
        background: T.card,
      }}
    >
      {/* Meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: T.fwSemibold, fontSize: T.fsBase, color: isExpired ? T.mid : T.dark,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
          title={session.sourceFilename}
        >
          {session.sourceFilename}
        </div>
        <div style={{ fontSize: T.fsXs, color: T.mid, marginTop: 2 }}>
          {SOURCE_LABELS[session.sourceType] || session.sourceType}
          {" · "}
          {fmtDate(session.uploadedAt)}
        </div>
      </div>

      {/* Diff count — show muted "Expired" badge for expired sessions */}
      <div style={{ width: 56, textAlign: "right", flexShrink: 0 }}>
        {isExpired ? (
          <span
            data-testid={`expired-badge-${session.id}`}
            style={{
              fontSize: T.fsXxs, fontWeight: T.fwBold,
              padding: "2px 8px", borderRadius: 10,
              background: T.lightGray, color: T.light,
              border: `1px solid ${T.border}`,
            }}
          >
            Expired
          </span>
        ) : (
          <>
            <div style={{ fontSize: T.fsLg6, fontWeight: T.fwDisplay, color: T.dark, lineHeight: 1 }}>
              {session.diffCount?.toLocaleString() ?? 0}
            </div>
            <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 1 }}>diffs</div>
          </>
        )}
      </div>

      {/* Status */}
      <div style={{ width: 80, textAlign: "center", flexShrink: 0 }}>
        <StatusPill status={session.status} />
      </div>

      {/* Actions */}
      <div style={{ width: 190, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        {/* Discard button — editor/admin only, staged/in_review/expired */}
        {canDiscard && (
          <button
            type="button"
            data-testid={`discard-btn-${session.id}`}
            onClick={() => onDiscard(session)}
            style={{
              padding: "6px 12px", borderRadius: 6, fontSize: T.fsSm,
              fontWeight: T.fwSemibold,
              border: `1.5px solid ${T.border}`,
              background: T.card, color: T.mid,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Discard
          </button>
        )}

        {/* Expired: Re-stage button opens session (shows expired modal with options) */}
        {isExpired ? (
          <button
            type="button"
            data-testid={`restage-btn-${session.id}`}
            onClick={() => onOpenSession(session.id)}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold,
              border: `1.5px solid ${T.border}`,
              background: T.card, color: T.mid,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Re-stage →
          </button>
        ) : (
          /* Review button — standard action-link pattern: filled soft-blue when
             actionable, visibly greyed/disabled when there is nothing to review. */
          <button
            type="button"
            data-testid={`review-btn-${session.id}`}
            disabled={!canReview}
            onClick={() => onOpenSession(session.id)}
            style={{
              padding: "6px 14px", borderRadius: 6, fontSize: T.fsSm, fontWeight: T.fwSemibold,
              border: `1.5px solid ${canReview ? T.blue : T.border}`,
              background: canReview ? T.blueSoft : T.lightGray,
              color: canReview ? T.blue : T.light,
              cursor: canReview ? "pointer" : "not-allowed",
              whiteSpace: "nowrap",
              opacity: canReview ? 1 : 0.6,
            }}
          >
            Review →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── PendingImportsQueue ──────────────────────────────────────────────────────

export function PendingImportsQueue({ onOpenSession, onBack }) {
  const [sessions,       setSessions]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [confirmTarget,  setConfirmTarget]  = useState(null); // null | { type:"single", session } | { type:"bulk", count }
  const [discarding,     setDiscarding]     = useState(false);
  const [searchQ,        setSearchQ]        = useState("");
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [filterOpen,     setFilterOpen]     = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listImportSessions()
      .then((data) => { if (!cancelled) { setSessions(data); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  const staged    = sessions.filter((s) => s.status === "staged").length;
  const inReview  = sessions.filter((s) => s.status === "in_review").length;
  const partial   = sessions.filter((s) => s.status === "partially_applied").length;
  const reviewable = staged + inReview + partial;
  const discardablePending = staged + inReview;

  // Statuses actually present in the current data set, in a stable order.
  const availableStatuses = useMemo(() => {
    const order = ["staged", "in_review", "partially_applied", "applied", "rejected", "uploading", "expired"];
    const present = new Set(sessions.map((s) => s.status));
    return order.filter((s) => present.has(s));
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (searchQ) {
        const q = searchQ;
        const matchesFilename = matchesFoldedQuery(q, s.sourceFilename);
        const matchesSource   = matchesFoldedQuery(q, SOURCE_LABELS[s.sourceType] || s.sourceType);
        if (!matchesFilename && !matchesSource) return false;
      }
      return true;
    });
  }, [sessions, statusFilter, searchQ]);

  // ── Discard handlers ────────────────────────────────────────────────────────

  function handleDiscardClick(session) {
    setConfirmTarget({ type: "single", session });
  }

  function handleDiscardAllClick() {
    setConfirmTarget({ type: "bulk", count: discardablePending });
  }

  function handleCancelConfirm() {
    if (!discarding) setConfirmTarget(null);
  }

  async function handleConfirmDiscard() {
    if (!confirmTarget) return;
    setDiscarding(true);
    try {
      if (confirmTarget.type === "single") {
        await discardImportSession(confirmTarget.session.id);
      } else {
        await discardAllPendingSessions();
      }
      setConfirmTarget(null);
      load();
    } catch (err) {
      setError(err);
      setConfirmTarget(null);
    } finally {
      setDiscarding(false);
    }
  }

  // ── Confirm dialog message ───────────────────────────────────────────────────

  const confirmMessage = confirmTarget?.type === "single"
    ? "Discard this staged import? No lease data is changed."
    : `Discard all ${confirmTarget?.count ?? ""} staged imports? No lease data is changed.`;

  return (
    <>
      {confirmTarget && (
        <ConfirmDialog
          message={confirmMessage}
          onConfirm={handleConfirmDiscard}
          onCancel={handleCancelConfirm}
          busy={discarding}
        />
      )}

      <div style={{ maxWidth: "1400px", padding: "24px 32px 40px", margin: "0 auto", width: "100%" }}>
        {/* Header */}
        <div style={{ marginBottom: 8 }}>
          <h1 style={{ fontSize: T.fsLgX, fontWeight: T.fwDisplay, color: T.dark, margin: 0 }}>
            Pending Imports
          </h1>
          <p style={{ fontSize: T.fsBase, color: T.mid, margin: "4px 0 0" }}>
            {reviewable > 0
              ? `${reviewable} session${reviewable === 1 ? "" : "s"} awaiting review`
              : "No sessions awaiting review"}
          </p>
        </div>

        {/* Back link — standard muted chevron + label, left-aligned under heading */}
        <button
          type="button"
          data-testid="back-btn"
          onClick={onBack}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: T.mid, fontSize: T.fsSm, fontWeight: T.fwSemibold, padding: 0,
            display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 20,
          }}
        >
          ← Back to Admin / Data
        </button>

        {/* Toolbar — Search (left), Filter, session count (right) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <SearchInput
            value={searchQ}
            onChange={setSearchQ}
            placeholder="Search file or source…"
            testId="pending-imports-search-input"
            width={240}
          />

          <div style={{ position: "relative" }}>
            <button
              type="button"
              data-testid="pending-imports-filter-btn"
              onClick={() => setFilterOpen((o) => !o)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 13px", borderRadius: 20,
                fontSize: T.fsXs, fontWeight: T.fwSemibold, fontFamily: "inherit",
                cursor: "pointer",
                border: `1px solid ${statusFilter !== "all" ? T.blue : T.border}`,
                background: statusFilter !== "all" ? T.blueSoft : T.card,
                color: statusFilter !== "all" ? T.blue : T.mid,
              }}
            >
              Filter{statusFilter !== "all" ? " · 1" : ""}
            </button>

            {filterOpen && (
              <div
                data-testid="pending-imports-filter-panel"
                style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 10,
                  background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: 14,
                  display: "flex", flexDirection: "column", gap: 10, minWidth: 200,
                }}
              >
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: T.fsXs, color: T.mid }}>
                  Status
                  <select
                    data-testid="pending-imports-status-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ padding: "4px 6px", borderRadius: 6, border: `1px solid ${T.border}`, fontFamily: "inherit" }}
                  >
                    <option value="all">All statuses</option>
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>{STATUS_PILL[s]?.label ?? s}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  data-testid="pending-imports-filter-reset-btn"
                  onClick={() => setStatusFilter("all")}
                  style={{
                    alignSelf: "flex-start", border: "none", background: "transparent",
                    color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer", padding: 0,
                  }}
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
            {discardablePending >= 2 && (
              <div data-testid="discard-all-bar">
                <button
                  type="button"
                  data-testid="discard-all-btn"
                  onClick={handleDiscardAllClick}
                  style={{
                    padding: "6px 14px", borderRadius: 6, fontSize: T.fsSm,
                    fontWeight: T.fwSemibold,
                    border: `1.5px solid ${T.border}`,
                    background: T.card, color: T.mid,
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  Discard all pending ({discardablePending})
                </button>
              </div>
            )}
            <span data-testid="pending-imports-count" style={{ fontSize: T.fsSm, color: T.mid, whiteSpace: "nowrap" }}>
              {filteredSessions.length} session{filteredSessions.length === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <Card style={{ padding: 0, overflow: "hidden" }}>
          {loading && (
            <div style={{ padding: 32, textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
              Loading import sessions…
            </div>
          )}

          {!loading && error && (
            <div style={{ padding: 20, color: T.errorText, fontSize: T.fsBase }}>
              Failed to load sessions: {error?.message || String(error)}
            </div>
          )}

          {!loading && !error && sessions.length === 0 && (
            <div style={{ padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: T.fsXl6, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: T.fsMd, fontWeight: T.fwSemibold, color: T.dark }}>
                No import sessions
              </div>
              <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 4 }}>
                Upload a CSV from the Leases page to stage diffs for review.
              </div>
            </div>
          )}

          {!loading && !error && sessions.length > 0 && filteredSessions.length === 0 && (
            <div style={{ padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: T.fsMd, fontWeight: T.fwSemibold, color: T.dark }}>
                No sessions match your search
              </div>
              <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 4 }}>
                Try a different search term or reset the filter.
              </div>
            </div>
          )}

          {!loading && !error && filteredSessions.length > 0 && (
            <OverflowGrid minWidth={760}>
              {/* Column header — standard uppercase header row */}
              <div
                role="row"
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 16px",
                  background: T.bg,
                  borderBottom: `1px solid ${T.border}`,
                  fontSize: T.fsXxs, fontWeight: T.fwBold,
                  color: T.light, letterSpacing: "0.05em", textTransform: "uppercase",
                }}
              >
                <div style={{ flex: 1 }}>File / source</div>
                <div style={{ width: 56, textAlign: "right" }}>Diffs</div>
                <div style={{ width: 80, textAlign: "center" }}>Status</div>
                <div style={{ width: 190 }} />
              </div>
              {filteredSessions.map((s) => (
                <SessionRow
                  key={s.id}
                  session={s}
                  onOpenSession={onOpenSession}
                  onDiscard={handleDiscardClick}
                />
              ))}
            </OverflowGrid>
          )}
        </Card>
      </div>
    </>
  );
}
